import { OptionalMark } from "./components/FormDisplayContext";
import { Button } from "@patient-watch/ui/button";

import { ui } from "@patient-watch/ui";
import { Input } from "@patient-watch/ui/input";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import DateInput from "./DateInput";
import { DEFAULT_DATETIME_TIME } from "./dateTimeDefaults";
import GroupedFields from "./GroupedFields";

function applyTimeToDate(date: Date, time: string) {
    const [hours, minutes] = time.split(":");
    return dayjs(date)
        .set("hour", Number.parseInt(hours as string, 10))
        .set("minute", Number.parseInt(minutes as string, 10))
        .set("second", 0)
        .set("millisecond", 0);
}

interface DateTimeInputProps {
    name: string;
    label?: string;
    description?: string;
    disabled?: boolean;
    required?: boolean;
    datePlaceholder?: string;
    clearLabel?: string;
    /** Inclusive earliest allowed calendar day. */
    minDate?: Date;
    /** Inclusive latest allowed calendar day. */
    maxDate?: Date;
    /** Shown when the typed date is earlier than minDate. */
    minMessage?: string;
    /** Shown when the typed date is later than maxDate. */
    maxMessage?: string;
}

const DateTimeInput: React.FC<DateTimeInputProps> = ({
    name,
    label,
    description,
    disabled,
    required = false,
    datePlaceholder = "Pick a date",
    clearLabel = "Clear Date and Time",
    minDate,
    maxDate,
    minMessage,
    maxMessage,
}) => {
    const { control, setValue } = useFormContext();
    const [date, setDate] = useState<Date | null | undefined>(undefined);
    const [time, setTime] = useState<string | undefined>(undefined);

    const dateTime = useWatch({
        control,
        name,
    });

    const updateDateTime = useCallback(
        (newDate?: Date | null, newTime?: string) => {
            if (newDate && newTime) {
                setValue(name, applyTimeToDate(newDate, newTime).toISOString());
            } else if (newDate) {
                setValue(name, applyTimeToDate(newDate, DEFAULT_DATETIME_TIME).toISOString());
            } else if (newTime) {
                setValue(name, applyTimeToDate(dayjs().startOf("day").toDate(), newTime).toISOString());
            } else {
                setValue(name, null);
            }
        },
        [name, setValue]
    );

    useEffect(() => {
        if (dateTime) {
            setDate(dayjs(dateTime).toDate());
            setTime(dayjs(dateTime).format("HH:mm"));
        }
    }, [dateTime]);

    useEffect(() => {
        updateDateTime(date, time);
    }, [date, time, updateDateTime]);

    return (
        <div>
            {label && (
                <ui.FormLabel className="block font-medium text-gray-700 text-sm">
                    {label}
                    {!required && <OptionalMark />}
                </ui.FormLabel>
            )}
            {description && (
                <ui.FormDescription className="mt-1 text-gray-500 text-sm">{description}</ui.FormDescription>
            )}
            <GroupedFields>
                <DateInput
                    value={date as Date}
                    onChange={(newDate) => {
                        setDate(newDate);
                        if (newDate && !time) {
                            setTime(DEFAULT_DATETIME_TIME);
                        }
                    }}
                    disabled={disabled}
                    placeholder={datePlaceholder}
                    minDate={minDate}
                    maxDate={maxDate}
                    minMessage={minMessage}
                    maxMessage={maxMessage}
                />
                <Input
                    data-testid="time-input-trigger"
                    className="bg-slate-50 dark:bg-slate-900 w-full"
                    autoComplete="time"
                    type="time"
                    value={time ?? (date ? DEFAULT_DATETIME_TIME : "")}
                    onChange={(e) => setTime(e.target.value)}
                    disabled={disabled}
                />
            </GroupedFields>
            <Button
                className="mt-2"
                size="sm"
                type="button"
                variant="link"
                onClick={() => {
                    setDate(undefined);
                    setTime("");
                    updateDateTime(null, undefined);
                }}
                disabled={disabled}
            >
                {clearLabel}
            </Button>
        </div>
    );
};

export default DateTimeInput;
